import React from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import OpenInNew from '@mui/icons-material/OpenInNew'
import type { AssistantPreviewData } from './useAssistantChat'

const KIND_LABEL: Record<AssistantPreviewData['kind'], string> = {
  inventory: 'Inventory',
  lead: 'Lead',
  page: 'Page',
  application: 'Application',
}

interface AssistantPreviewCardProps {
  preview: AssistantPreviewData
  onOpen: (path: string) => void
}

export const AssistantPreviewCard: React.FC<AssistantPreviewCardProps> = ({
  preview,
  onOpen,
}) => (
  <Box
    data-assistant-demo='preview'
    sx={{
      mt: 1.25,
      p: 1.25,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction='row' spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
          {KIND_LABEL[preview.kind] || 'Portal'}
        </Typography>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, mt: 0.25 }}>
          {preview.title}
        </Typography>
        {preview.subtitle ? (
          <Typography variant='body2' color='text.secondary'>
            {preview.subtitle}
          </Typography>
        ) : null}
      </Box>
      <Button
        size='small'
        variant='outlined'
        startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
        onClick={() => onOpen(preview.path)}
        sx={{
          flexShrink: 0,
          minHeight: 28,
          px: 1,
          py: 0.25,
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'none',
          '& .MuiButton-startIcon': { mr: 0.5 },
        }}
      >
        Open
      </Button>
    </Stack>
    {preview.imageUrl ? (
      <Box
        component='img'
        src={preview.imageUrl}
        alt=''
        sx={{
          display: 'block',
          width: '100%',
          maxWidth: 420,
          maxHeight: 220,
          objectFit: 'contain',
          borderRadius: 1,
          mt: 1,
        }}
      />
    ) : null}
    {preview.facts && preview.facts.length > 0 ? (
      <Stack spacing={0.25} sx={{ mt: 1 }}>
        {preview.facts.map(fact => (
          <Typography key={fact.label} variant='body2'>
            <Box component='span' sx={{ color: 'text.secondary' }}>
              {fact.label}:{' '}
            </Box>
            {fact.value}
          </Typography>
        ))}
      </Stack>
    ) : null}
  </Box>
)

/** Shown only while Show Me is explaining preview cards. Open does not leave the tour. */
export const DEMO_PREVIEW_SAMPLE: AssistantPreviewData = {
  kind: 'inventory',
  title: '2020 Freightliner Cascadia',
  subtitle: 'Stock 1042 · Sleeper',
  path: '/admin/inventory',
  facts: [
    { label: 'Status', value: 'Available' },
    { label: 'Price', value: '$42,500' },
    { label: 'Miles', value: '412,000' },
  ],
}

export const DemoPreviewSample: React.FC = () => (
  <Box>
    <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
      Example
    </Typography>
    <AssistantPreviewCard preview={DEMO_PREVIEW_SAMPLE} onOpen={() => undefined} />
  </Box>
)

export const AssistantPreviewList: React.FC<{
  previews: AssistantPreviewData[]
  onOpen: (path: string) => void
}> = ({ previews, onOpen }) => (
  <>
    {previews.map((preview, index) => (
      <AssistantPreviewCard
        key={`${preview.path}-${index}`}
        preview={preview}
        onOpen={onOpen}
      />
    ))}
  </>
)
